import { IAuthenticationContext, withAuthentication } from "./authentication";
import { Link, Navigate, useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from 'react';

import ContactMethodInput from "./contact_method_input";
import WelcomeHeader from "./welcome_header";
import countryList from 'react-select-country-list'
import { grpcAPI } from "./api";

interface Props extends IAuthenticationContext {

}

function SignUpPage(props: Props) {
    const [contactMethod, setContactMethod] = useState<'phone' | 'email'>('phone');
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [confirmPhoneNumber, setConfirmPhoneNumber] = useState<string>("");
    const [emailAddress, setEmailAddress] = useState<string>("");
    const [confirmEmailAddress, setConfirmEmailAddress] = useState<string>("");
    const [country, setCountry] = useState<string>("");
    const [countrySearchTerm, setCountrySearchTerm] = useState<string>("");
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState<boolean>(false);
    const [selectedCountryIndex, setSelectedCountryIndex] = useState<number>(-1);
    const [ageRange, setAgeRange] = useState<string>("");
    const [gender, setGender] = useState<string>("");
    const [genderOther, setGenderOther] = useState<string>("");
    const [sexualOrientation, setSexualOrientation] = useState<string>("");
    const [sexualityOther, setSexualityOther] = useState<string>("");
    const [indigenousStatus, setIndigenousStatus] = useState<string>("");
    const [australiaResidencyDuration, setAustraliaResidencyDuration] = useState<string>("");
    const [hasTestedHivBefore, setHasTestedHivBefore] = useState<boolean | undefined>(undefined);
    const [isAbove18, setIsAbove18] = useState<boolean>(false);
    const [researchAndReporting, setResearchAndReporting] = useState<boolean>(false);
    const [isAgreePolicyAndAgreement, setIsAgreePolicyAndAgreement] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const countryDropdownRef = useRef<HTMLDivElement>(null);
    const countryData = useMemo(() => countryList().getData(), []);
    const filteredCountries = useMemo(() => {
        if (!countrySearchTerm) return countryData;
        return countryData.filter(country =>
            country.label.toLowerCase().includes(countrySearchTerm.toLowerCase())
        );
    }, [countryData, countrySearchTerm]);

    const ageRangeOptions = [
        <option key="24_and_under" value="24_and_under">24 and under</option>,
        <option key="25_29" value="25_29">25-29</option>,
        <option key="30_39" value="30_39">30-39</option>,
        <option key="40_49" value="40_49">40-49</option>,
        <option key="50_59" value="50_59">50-59</option>,
        <option key="60_and_older" value="60_and_older">60 and older</option>
    ];

    const navigate = useNavigate()

    // Initialize search term with country value when component mounts
    useEffect(() => {
        if (country && !countrySearchTerm) {
            setCountrySearchTerm(country);
        }
    }, [country, countrySearchTerm]);

    // Handle clicking outside dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
                setIsCountryDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleResearchAndReporting = (e: any) => {
        setResearchAndReporting(e.target.checked)
    }

    const handleCountrySelect = (countryName: string) => {
        setCountry(countryName);
        setCountrySearchTerm(countryName);
        setIsCountryDropdownOpen(false);
        // Reset conditional fields when country changes
        setIndigenousStatus("");
        setAustraliaResidencyDuration("");
    };

    const handleCountrySearchChange = (e: any) => {
        setCountrySearchTerm(e.target.value);
        setIsCountryDropdownOpen(true);
        setSelectedCountryIndex(-1);
        // If the typed value exactly matches a country, select it
        const exactMatch = countryData.find(country =>
            country.label.toLowerCase() === e.target.value.toLowerCase()
        );
        if (exactMatch && exactMatch.label !== country) {
            setCountry(exactMatch.label);
            setIndigenousStatus("");
            setAustraliaResidencyDuration("");
        } else if (!exactMatch && country) {
            // Clear country if no exact match and we had a country selected
            setCountry("");
            setIndigenousStatus("");
            setAustraliaResidencyDuration("");
        }
    };

    const handleCountryInputFocus = () => {
        setIsCountryDropdownOpen(true);
    };

    const handleCountryKeyDown = (e: any) => {
        if (!isCountryDropdownOpen) return;

        const visibleCountries = filteredCountries.slice(0, 10);

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedCountryIndex(prev =>
                prev < visibleCountries.length - 1 ? prev + 1 : 0
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedCountryIndex(prev =>
                prev > 0 ? prev - 1 : visibleCountries.length - 1
            );
        } else if (e.key === 'Enter' && selectedCountryIndex >= 0) {
            e.preventDefault();
            handleCountrySelect(visibleCountries[selectedCountryIndex].label);
        } else if (e.key === 'Escape') {
            setIsCountryDropdownOpen(false);
            setSelectedCountryIndex(-1);
        }
    };

    const handleAgeRangeChange = (e: any) => {
        setAgeRange(e.target.value)
    }

    const handleSexualOrientationChange = (e: any) => {
        setSexualOrientation(e.target.value)
        if (e.target.value !== "other") {
            setSexualityOther("")
        }
    }

    const handleSexualityOtherChange = (e: any) => {
        setSexualityOther(e.target.value)
    }

    const handleGenderChange = (e: any) => {
        setGender(e.target.value)
        if (e.target.value !== "other") {
            setGenderOther("")
        }
    }

    const handleGenderOtherChange = (e: any) => {
        setGenderOther(e.target.value)
    }

    const handleIndigenousStatusChange = (e: any) => {
        setIndigenousStatus(e.target.value)
    }

    const handleAustraliaResidencyDurationChange = (e: any) => {
        setAustraliaResidencyDuration(e.target.value)
    }

    const handleHasTestedHivBefore = (e: any) => {
        if (e.currentTarget.value === "yes") {
            setHasTestedHivBefore(true)
        }
        if (e.currentTarget.value === "no") {
            setHasTestedHivBefore(false)
        }
    }

    const handleIsAbove18Change = (e: any) => {
        setIsAbove18(e.target.checked)
    }

    const handleIsAgreePolicyAndAgreementChange = (e: any) => {
        setIsAgreePolicyAndAgreement(e.target.checked)
    }

    const signup = (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        if (isLoading) {
            return;
        }
        const apiService = new grpcAPI();
        if (contactMethod === 'phone' && phoneNumber === "") {
            window.alert("Mobile Number can't be empty")
            return;
        }
        if (contactMethod === 'phone' && !/^\+614\d{8}$/.test(phoneNumber)) {
            window.alert("Please enter a valid Australian mobile number (10 digits)")
            return;
        }
        if (contactMethod === 'phone' && phoneNumber !== confirmPhoneNumber) {
            window.alert("Mobile numbers do not match")
            return;
        }
        if (contactMethod === 'email' && emailAddress.trim() === "") {
            window.alert("Please enter an email address")
            return;
        }
        if (contactMethod === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
            window.alert("Please enter a valid email address")
            return;
        }
        if (contactMethod === 'email' && emailAddress !== confirmEmailAddress) {
            window.alert("Email addresses do not match")
            return;
        }
        if (country === "") {
            window.alert("Please select Country of Birth")
            return;
        }
        if (ageRange === "") {
            window.alert("Please select your age range")
            return;
        }
        if (gender === "") {
            window.alert("Please select Gender")
            return;
        }
        if (gender === "other" && genderOther === "") {
            window.alert("Please specify your gender")
            return;
        }
        if (sexualOrientation === "") {
            window.alert("Please select Sexual Orientation")
            return;
        }
        if (sexualOrientation === "other" && sexualityOther === "") {
            window.alert("Please specify your sexual orientation")
            return;
        }
        if (country === "Australia" && indigenousStatus === "") {
            window.alert("Please select Indigenous Status")
            return;
        }
        if (country !== "Australia" && australiaResidencyDuration === "") {
            window.alert("Please select how long you have lived in Australia")
            return;
        }
        if (hasTestedHivBefore === undefined) {
            window.alert("Please select Have you ever tested for HIV before")
            return;
        }
        if (!isAbove18) {
            window.alert("Please confirm you are over 18")
            return;
        }
        if (!isAgreePolicyAndAgreement) {
            window.alert("Please confirm you have read and agreed privacy policy")
            return;
        }
        setIsLoading(true);
        const isEmailSignup = contactMethod === 'email';
        apiService.hivTestAPIServiceSignup({
            mobile: isEmailSignup ? undefined : phoneNumber,
            email: isEmailSignup ? emailAddress : undefined,
            countryOfBirth: country,
            ageRange: ageRange,
            sexOrientation: sexualOrientation,
            sexualityOther: sexualOrientation === "other" ? sexualityOther : undefined,
            gender: gender,
            genderOther: gender === "other" ? genderOther : undefined,
            indigenousStatus: country === "Australia" ? indigenousStatus : undefined,
            australiaResidencyDuration: country !== "Australia" ? australiaResidencyDuration : undefined,
            testedBefore: hasTestedHivBefore,
            overEighteen: isAbove18,
            agreement: isAgreePolicyAndAgreement,
            dataUseAgreement: researchAndReporting
        }).then((response) => {
            if (response.data.success) {
                if (isEmailSignup) {
                    localStorage.setItem("email", emailAddress);
                } else {
                    localStorage.setItem("mobile", phoneNumber);
                }
                localStorage.setItem("registrationMethod", contactMethod);
                localStorage.setItem("challengeCode", response.data.challengeCode ?? "");
                setIsLoading(false)
                navigate("/verification", { replace: true })
            } else {
                window.alert(response.data.errorMessage)
            }
        }).catch((reason) => {
            window.alert("Unknown server error, please retry")
        }).finally(() => {
            setIsLoading(false)
        })
    }

    if (props.authentication.authenticated) {
        return (<Navigate to="/" />);
    }

    return (
        <div>
            <WelcomeHeader />
            <div className={"backgroundSecondary py-3"}>
                <div className={"container"}>
                    <div className={"pt-3 footText1"}>
                        <p>
                            Already have an account? <Link className={"textLink textUnderline"} to={"/sign_in"}>Sign in here</Link>
                        </p>
                    </div>
                    <ContactMethodInput
                        contactMethod={contactMethod}
                        onContactMethodChange={setContactMethod}
                        phoneNumber={phoneNumber}
                        onPhoneNumberChange={setPhoneNumber}
                        emailAddress={emailAddress}
                        onEmailAddressChange={setEmailAddress}
                        confirmPhoneNumber={confirmPhoneNumber}
                        onConfirmPhoneNumberChange={setConfirmPhoneNumber}
                        confirmEmailAddress={confirmEmailAddress}
                        onConfirmEmailAddressChange={setConfirmEmailAddress}
                    />
                    <div className="form-floating mb-3" ref={countryDropdownRef}>
                        <input
                            id="countryInput"
                            type="text"
                            className="form-select border-0 border-bottom"
                            value={countrySearchTerm}
                            onChange={handleCountrySearchChange}
                            onFocus={handleCountryInputFocus}
                            onKeyDown={handleCountryKeyDown}
                            placeholder="Search for a country..."
                            autoComplete="off"
                        />
                        <label htmlFor="countryInput" className={"textSecondary"}>Country of Birth</label>
                        {isCountryDropdownOpen && filteredCountries.length > 0 && (
                            <div className="dropdown-menu show w-100" style={{
                                maxHeight: '200px',
                                overflowY: 'auto',
                                position: 'absolute',
                                top: '100%',
                                left: '0',
                                zIndex: 1000
                            }}>
                                {filteredCountries.slice(0, 10).map((countryOption, index) => (
                                    <button
                                        key={countryOption.value}
                                        type="button"
                                        className={`dropdown-item ${index === selectedCountryIndex ? 'active' : ''}`}
                                        onClick={() => handleCountrySelect(countryOption.label)}
                                    >
                                        {countryOption.label}
                                    </button>
                                ))}
                                {filteredCountries.length > 10 && (
                                    <div className="dropdown-item-text text-muted small">
                                        Showing first 10 results. Keep typing to narrow down...
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {country === "Australia" && (
                        <div className="form-floating mb-3">
                            <select id="indigenousStatusSelect" className="form-select border-0 border-bottom" aria-label="Floating" value={indigenousStatus} onChange={handleIndigenousStatusChange}>
                                <option hidden value="" />
                                <option value={"no"}>No</option>
                                <option value={"aboriginal"}>Aboriginal</option>
                                <option value={"torres_strait_islander"}>Torres Strait Islander</option>
                                <option value={"both"}>Both Aboriginal and Torres Strait Islander</option>
                            </select>
                            <label htmlFor="indigenousStatusSelect" className={"textSecondary"}>Indigenous Status</label>
                        </div>
                    )}
                    {country !== "Australia" && country !== "" && (
                        <div className="form-floating mb-3">
                            <select id="australiaResidencySelect" className="form-select border-0 border-bottom" aria-label="Floating" value={australiaResidencyDuration} onChange={handleAustraliaResidencyDurationChange}>
                                <option hidden value="" />
                                <option value={"less_than_12_months"}>Less than 12 months</option>
                                <option value={"1_2_years"}>1-2 years</option>
                                <option value={"3_4_years"}>3-4 years</option>
                                <option value={"5_years_or_more"}>5 years or more</option>
                            </select>
                            <label htmlFor="australiaResidencySelect" className={"textSecondary"}>How long have you lived in Australia?</label>
                        </div>
                    )}
                    <div className="form-floating mb-3">
                        <select id="ageRangeSelect" className="form-select border-0 border-bottom" aria-label="Floating" value={ageRange} onChange={handleAgeRangeChange}>
                            <option hidden value="" />
                            {ageRangeOptions}
                        </select>
                        <label htmlFor="ageRangeSelect" className={"textSecondary"}>How old are you?</label>
                    </div>
                    <div className="form-floating mb-3">
                        <select id="genderSelect" className="form-select border-0 border-bottom" aria-label="Floating" value={gender} onChange={handleGenderChange}>
                            <option hidden value="" />
                            <option value={"male_or_man"}>Male/Man</option>
                            <option value={"female_or_woman"}>Female/Woman</option>
                            <option value={"nonbinary"}>Non-binary</option>
                            <option value={"other"}>I use a different term (Please describe)</option>
                        </select>
                        <label htmlFor="genderSelect" className={"textSecondary"}>Gender</label>
                    </div>
                    {gender === "other" && (
                        <div className="form-floating mb-3">
                            <input id="genderOtherInput" type="text" className="form-control border-0 border-bottom" value={genderOther} onChange={handleGenderOtherChange} />
                            <label htmlFor="genderOtherInput" className={"textSecondary"}>Please specify your gender</label>
                        </div>
                    )}
                    <div className="form-floating mb-3">
                        <select id="sexualOrientationSelect" className="form-select border-0 border-bottom" aria-label="Floating" value={sexualOrientation} onChange={handleSexualOrientationChange}>
                            <option hidden value="" />
                            <option value={"gay"}>Gay</option>
                            <option value={"lesbian"}>Lesbian</option>
                            <option value={"heterosexual"}>Heterosexual</option>
                            <option value={"bisexual"}>Bisexual</option>
                            <option value={"pansexual"}>Pansexual</option>
                            <option value={"queer"}>Queer</option>
                            <option value={"questioning"}>Questioning</option>
                            <option value={"other"}>I use a different term (Please specify)</option>
                        </select>
                        <label htmlFor="sexualOrientationSelect" className={"textSecondary"}>Sexual Orientation</label>
                    </div>
                    {sexualOrientation === "other" && (
                        <div className="form-floating mb-3">
                            <input id="sexualityOtherInput" type="text" className="form-control border-0 border-bottom" value={sexualityOther} onChange={handleSexualityOtherChange} />
                            <label htmlFor="sexualityOtherInput" className={"textSecondary"}>Please specify your sexual orientation</label>
                        </div>
                    )}
                    <div>
                        <label className={"textSecondary"} >Have you ever tested for HIV before?</label>
                        <div className="form-check">
                            <input className="form-check-input" type="radio" name="flexRadioDefault" id="flexRadioDefault1" value={"yes"} checked={hasTestedHivBefore === true} onChange={handleHasTestedHivBefore} />
                            <label className="form-check-label textSecondary" htmlFor="flexRadioDefault1">
                                Yes
                            </label>
                        </div>
                        <div className="form-check">
                            <input className="form-check-input" type="radio" name="flexRadioDefault" id="flexRadioDefault2" value={"no"} checked={hasTestedHivBefore === false} onChange={handleHasTestedHivBefore} />
                            <label className="form-check-label textSecondary" htmlFor="flexRadioDefault2">
                                No
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div className={"backgroundPrimary py-4"}>
                <div className={"container"}>
                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="flexCheckDefault" checked={isAbove18} onChange={handleIsAbove18Change} />
                        <label className="form-check-label textPrimary" htmlFor="flexCheckDefault">
                            I am over 18
                        </label>
                    </div>

                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="flexCheckAgree" checked={researchAndReporting} onChange={handleResearchAndReporting} />
                        <label className="form-check-label textPrimary" htmlFor="flexCheckAgree">
                            I agree to my de-identified data being used for research purposes including publication.
                        </label>
                    </div>

                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" value="" id="flexCheckChecked" checked={isAgreePolicyAndAgreement} onChange={handleIsAgreePolicyAndAgreementChange} />
                        <label className="form-check-label textPrimary" htmlFor="flexCheckChecked">
                            I have read and agreed <a className={"textLink"} href={"https://hivconnect.org.au/privacy-policy"}>privacy policy</a>.
                        </label>
                    </div>
                    <div className={"container pt-3 text-center"}>
                        <div className="d-grid mw-100 buttonPrimary rounded">
                            <button className="textButton btn" onClick={signup}>
                                {!isLoading && <span>Proceed</span>}
                                {isLoading && <i className="fas fa-spinner fa-spin" />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default withAuthentication(SignUpPage);
